import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useUIDSeed } from 'react-uid';

import { toCurrency } from 'utils/numbers';

import Plan, { PlanType } from 'models/Plan';
import { getQueryParam } from 'utils/location';

import preparedImgUrl from 'consumer/signup/plans/prepared.jpg';
import preparedImgUrl2x from 'consumer/signup/plans/prepared@2x.jpg';
import mealKitImgUrl from 'consumer/signup/plans/meal_kit.jpg';
import mealKitImgUrl2x from 'consumer/signup/plans/meal_kit@2x.jpg';
import programsImgUrl from 'consumer/signup/plans/programs.jpg';
import programsImgUrl2x from 'consumer/signup/plans/programs@2x.jpg';

import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import Modal from 'components/shared/Modal';
import MealPreferencesModal from './MealPreferencesModal';
import PlanConfirmationModal from './PlanConfirmationModal';

type Props = {
  show: boolean;
  standalone: boolean;
  discount: number;
  plan: Plan;
  setPlan: (plan: Plan) => void;
  onClose: () => void;
  discountCouponType?: 'dollar_off' | 'percentage_off';
  discountCouponValue?: number;
  areProgramsEnabled: boolean;
  handlePlanChange: () => void;
  renderFollowUps: boolean
};

function SelectPlanModal({
  show,
  standalone,
  discount,
  plan,
  setPlan,
  onClose,
  discountCouponType,
  discountCouponValue,
  areProgramsEnabled = false,
  handlePlanChange,
  renderFollowUps = false,
}: Props): ReactElement {
  const seed = useUIDSeed();

  const servingOptions = {
    [PlanType.MEAL_KIT]: Plan.getServingOptions(PlanType.MEAL_KIT),
    [PlanType.PREPARED]: Plan.getServingOptions(PlanType.PREPARED),
    [PlanType.PROGRAMS]: Plan.getServingOptions(PlanType.PROGRAMS),
    [PlanType.DIVERSIFIED]: Plan.getServingOptions(PlanType.MEAL_KIT),
  };

  const extraDinnerOptions = {
    [PlanType.MEAL_KIT]: [0, 1],
    [PlanType.PREPARED]: [0, 2, 4],
  };

  const [showPlanConfirmation, setShowPlanConfirmation] = useState(false);
  const [showMealPreferences, setShowMealPreferences] = useState(
    getQueryParam('edit') === 'plan_preference',
  );

  let weeklyPrice;
  let planPrice;
  switch (plan.type) {
    case PlanType.MEAL_KIT:
      weeklyPrice = plan.planPrice + plan.extraDinners * plan.servingPrice * plan.servings;
      planPrice = plan.servings * plan.servingPrice * plan.includedDinners;
      break;
    case PlanType.PROGRAMS:
      weeklyPrice = 130;
      planPrice = 130;
      break;
    default:
      weeklyPrice = (plan.includedDinners + plan.extras) * plan.servingPrice;
      planPrice = plan.servings * plan.servingPrice * plan.includedDinners;
      break;
  }
  const discountAmount = useMemo(() => {
    if (discountCouponType === 'percentage_off') {
      // @ts-expect-error TODO: fix null safety
      return (discountCouponValue / 100) * weeklyPrice;
    }

    return discount;
  }, [discount, weeklyPrice, discountCouponType, discountCouponValue]);

  const breakfastAndLunchPrice = plan.type === PlanType.PROGRAMS ? 0 : plan.breakfastAndLunchPrice;

  const firstWeekTotal = weeklyPrice + breakfastAndLunchPrice - discountAmount;

  const setPlanByType = (planType: PlanType) => {
    if (plan.type !== planType) {
      let newPlan;
      switch (planType) {
        case PlanType.PREPARED:
          newPlan = new Plan('prepared_one_serving');
          break;
        case PlanType.PROGRAMS:
          newPlan = new Plan('jumpstart_program');
          break;
        case PlanType.DIVERSIFIED:
          newPlan = new Plan('high_fiber');
          break;
        default:
          newPlan = new Plan('chefs_choice');
          break;
      }
      Plan.initialize([]);
      setPlan(newPlan);
    }
  };

  const setPlanAndServings = (planType: PlanType, servingSize: number) => {
    let newPlan;
    switch (planType) {
      case PlanType.MEAL_KIT:
        if (servingSize === 2) {
          newPlan = new Plan('chefs_choice');
        } else {
          newPlan = new Plan('four_servings');
        }
        break;
      case PlanType.PREPARED:
        newPlan = new Plan('prepared_one_serving');
        break;
      default:
        newPlan = new Plan('jumpstart_program');
        break;
    }
    setPlan(newPlan);
  };

  const setPlanWithExtraDinners = async (extraDinners: number) => {
    if (extraDinners !== plan.extraDinners) {
      const newPlan = Plan.getPlan(plan.servings, {
        ...plan.selectedExtras,
        dinner: extraDinners,
      }, plan.name);

      setPlan(newPlan);
    }
  };

  const closeAndShowConfirmation = () => {
    setShowMealPreferences(false);
    setShowPlanConfirmation(true);
  };

  return (
    <>
      <Modal
        show={show}
        onClose={onClose}
        className="c-modal-plan-select"
        body={(
          <>
            <button
              className="close"
              type="button"
              aria-label="Close modal"
              onClick={onClose}
            >
              <CloseIcon width="32" height="32" />
            </button>

            <div
              className={[
                'c-modal-plan-select__title',
                'c-heading',
                'c-heading--brand-4',
              ].join(' ')}
            >
              Select Plan
            </div>

            <div className="c-plan c-plan--prepared">
              <h2 className="c-plan__subtitle u-text--center">
                <strong>
                  1. Choose your plan type
                </strong>
              </h2>

              <div className="d-flex justify-content-center">
                <button
                  type="button"
                  className={[
                    'c-card',
                    plan.type === PlanType.MEAL_KIT ? 'is-selected' : '',
                  ].join(' ')}
                  onClick={() => setPlanByType(PlanType.MEAL_KIT)}
                >
                  <img
                    src={mealKitImgUrl}
                    srcSet={`${mealKitImgUrl} 1x, ${mealKitImgUrl2x} 2x`}
                    alt=""
                  />
                  <h3 className="c-plan__title">Meal Kit</h3>
                </button>
                <button
                  type="button"
                  className={[
                    'c-card',
                    plan.type === PlanType.PREPARED ? 'is-selected' : '',
                  ].join(' ')}
                  onClick={() => setPlanByType(PlanType.PREPARED)}
                >
                  <img
                    src={preparedImgUrl}
                    srcSet={`${preparedImgUrl} 1x, ${preparedImgUrl2x} 2x`}
                    className="c-plan__image"
                    alt=""
                  />
                  <h3 className="c-plan__title">Ready-To-Eat Meals</h3>
                </button>
                { areProgramsEnabled && (
                  <button
                    type="button"
                    className={[
                      'c-card',
                      plan.type === PlanType.PROGRAMS ? 'is-selected' : '',
                    ].join(' ')}
                    onClick={() => setPlanByType(PlanType.PROGRAMS)}
                  >
                    <img
                      src={programsImgUrl}
                      srcSet={`${programsImgUrl} 1x, ${programsImgUrl2x} 2x`}
                      className="c-plan__image"
                      alt=""
                    />
                    <h3 className="c-plan__title">Plant-Based Jumpstart</h3>
                  </button>
                )}
              </div>
              <h2 className="c-plan__subtitle">
                <strong>
                  {plan.type !== PlanType.PROGRAMS ? '2. Customize your plan size' : '2. Plan Size'}
                </strong>
              </h2>
              <div
                className="c-plan__options is-selected"
              >
                {plan.type === PlanType.PROGRAMS ? (
                  <div className="c-plan__servings">
                    4 Ready to Eat Breakfasts,  4 Ready to Eat Lunches, 4 Ready to Eat Dinners
                  </div>
                ) : (
                  <>
                    <fieldset>
                      <div className="c-plan__servings c-plan__select-account-servings">
                        <legend id="plan-servings">
                          Number of servings per meal
                        </legend>
                        <ul
                          role="radiogroup"
                          className="c-switch"
                          aria-labelledby="plan-servings"
                        >
                          {servingOptions[plan.type].map((servings) => (
                            <li key={seed(servings)}>
                              <button
                                type="button"
                                role="radio"
                                aria-label={`Select ${servings} Serving`}
                                aria-checked={plan.servings === servings}
                                className={plan.servings === servings
                                  ? 'is-selected'
                                  : ''}
                                onClick={() => {
                                  setPlanAndServings(plan.type, servings);
                                }}
                              >
                                {servings}
                                <span className="sr-only">serving</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </fieldset>

                    {standalone && (
                      <fieldset>
                        <div className="c-plan__servings">
                          <legend id="plan-servings">
                            Meals per week
                          </legend>
                          <ul
                            role="radiogroup"
                            className="c-switch"
                            aria-labelledby="plan-servings"
                          >
                            {extraDinnerOptions[plan.type].map((extraDinners) => (
                              <li key={seed(extraDinners)}>
                                <button
                                  type="button"
                                  role="radio"
                                  aria-label={
                                    `Select ${plan.includedDinners + extraDinners} Meals`
                                  }
                                  aria-checked={plan.extraDinners === extraDinners}
                                  className={plan.extraDinners === extraDinners
                                    ? 'is-selected'
                                    : ''}
                                  onClick={() => setPlanWithExtraDinners(extraDinners)}
                                >
                                  {plan.includedDinners + extraDinners}
                                  <span className="sr-only">serving</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </fieldset>
                    )}
                  </>
                )}
                <div className="c-plan__options is-selected">
                  <div className="is-selected">
                    <div className="c-plan__details c-plan__account-details">
                      {plan.type !== PlanType.PROGRAMS && (
                        <div className="c-plan__price-details">
                          <div>Cost Per Serving</div>
                          <div>
                            {toCurrency(plan.servingPrice)}
                          </div>
                        </div>
                      )}
                      <div className="c-plan__price-details">
                        <div>Weekly price</div>
                        {discount ? (
                          <s>
                            {toCurrency(standalone ? weeklyPrice : planPrice)}
                          </s>
                        ) : (
                          <div>
                            {toCurrency(standalone ? weeklyPrice : planPrice)}
                          </div>
                        )}
                      </div>
                      {!!discount && (
                        <div className="c-plan__price-details">
                          <strong>First week (with discount)</strong>
                          <strong>
                            {toCurrency(Math.max(0, firstWeekTotal))}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    title="Select chefs_choice"
                    className={[
                      'c-plan__button',
                      'c-plan__select-plan-button',
                      'c-button',
                      'c-button--primary',
                      'd-block',
                    ].join(' ')}
                    onClick={() => {
                      if (!standalone && plan.type !== PlanType.PROGRAMS) {
                        if (plan.servings === 2) {
                          setShowMealPreferences(true);
                        }
                      } else {
                        // Programs only shows plan confirmation
                        setShowPlanConfirmation(true);
                      }
                      handlePlanChange();
                      onClose();
                    }}
                  >
                    Select This Plan
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      />

      {renderFollowUps && (
      <>
        <MealPreferencesModal
          show={showMealPreferences}
          plan={plan}
          setPlan={setPlan}
          extras={plan.selectedExtras}
          onClose={closeAndShowConfirmation}
          onConfirm={() => {
            handlePlanChange();
            setShowMealPreferences(false);
            setShowPlanConfirmation(true);
          }}
        />

        {showPlanConfirmation && (
        <PlanConfirmationModal
          plan={plan}
          onClose={() => setShowPlanConfirmation(false)}
        />
        )}
      </>
      )}
    </>
  );
}

export default SelectPlanModal;
